import { useState } from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, Plus, Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { C, fontSerif } from './utils';
import { incentivesAPI } from '../../lib/api';

const STATUS_STYLES = {
  SUBMITTED: 'bg-slate-100 text-slate-700 border-slate-200',
  UNDER_REVIEW: 'bg-blue-50 text-blue-800 border-blue-200',
  ACCEPTED: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  IMPLEMENTED: 'bg-teal-50 text-teal-800 border-teal-200',
  REJECTED: 'bg-rose-50 text-rose-800 border-rose-200',
};

const CATEGORIES = ['Process', 'Tooling', 'Yield / Quality', 'Automation', 'Other'];
const STATUSES = ['SUBMITTED', 'UNDER_REVIEW', 'ACCEPTED', 'IMPLEMENTED', 'REJECTED'];

export const ImprovementsView = ({ improvements, canSubmit, canReview, employeesById, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'Process', impact: '' });

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Give your idea a title'); return; }
    try {
      setSaving(true);
      await incentivesAPI.submitImprovement(form);
      toast.success('Idea submitted 🎉');
      setForm({ title: '', description: '', category: 'Process', impact: '' });
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit');
    } finally {
      setSaving(false);
    }
  };

  const review = async (id, status) => {
    try {
      await incentivesAPI.reviewImprovement(id, { status });
      toast.success(`Marked ${status.replace('_', ' ').toLowerCase()}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    }
  };

  return (
    <div className="space-y-6" data-testid="improvements-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold" style={{ fontFamily: fontSerif, color: C.text }}>Ideas & Improvements</h2>
          <p className="text-sm text-slate-500">Suggest process, tooling or yield improvements. Accepted ideas count towards KPIs.</p>
        </div>
        {canSubmit && (
          <Button
            onClick={() => setOpen(true)}
            className="rounded-full text-white"
            style={{ background: C.burgundy }}
            data-testid="submit-idea-button"
          >
            <Plus className="mr-1.5 h-4 w-4" /> New idea
          </Button>
        )}
      </div>

      {(!improvements || improvements.length === 0) ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border bg-white py-16 text-center" style={{ borderColor: C.border }}>
          <Lightbulb className="h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-500">No ideas yet</p>
          <p className="text-xs text-slate-400">Be the first to suggest an improvement.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {improvements.map((imp, i) => (
            <motion.div
              key={imp.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.04 }}
              className="rounded-2xl border bg-white p-5"
              style={{ borderColor: C.border }}
              data-testid={`improvement-${imp.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="font-semibold" style={{ color: C.text }}>{imp.title}</h4>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {imp.category}
                    {employeesById?.[imp.employee_id] && ` · ${employeesById[imp.employee_id]}`}
                  </p>
                </div>
                <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[imp.status] || STATUS_STYLES.SUBMITTED}`}>
                  {imp.status.replace('_', ' ')}
                </span>
              </div>
              {imp.description && <p className="mt-3 text-sm text-slate-600">{imp.description}</p>}
              {imp.impact && (
                <p className="mt-2 text-xs text-slate-500"><span className="font-medium">Impact:</span> {imp.impact}</p>
              )}
              {imp.comments && (
                <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                  <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  {imp.comments}
                </div>
              )}
              {canReview && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {STATUSES.filter((s) => s !== imp.status).map((s) => (
                    <button
                      key={s}
                      onClick={() => review(imp.id, s)}
                      className="rounded-full border px-2.5 py-1 text-[11px] font-medium text-slate-600 transition hover:bg-slate-50"
                      style={{ borderColor: C.border }}
                      data-testid={`review-${imp.id}-${s}`}
                    >
                      {s.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="idea-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: fontSerif }}>Share an idea</DialogTitle>
            <DialogDescription>Small improvements add up. What could we do better?</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Title</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Batch-render setups to save 20 min/day"
                className="mt-1"
                data-testid="idea-title-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="idea-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What's the problem and your suggested fix?"
                rows={3}
                className="mt-1"
                data-testid="idea-description-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Expected impact (optional)</label>
              <Input
                value={form.impact}
                onChange={(e) => setForm({ ...form, impact: e.target.value })}
                placeholder="e.g. +1 set/day, fewer revisions"
                className="mt-1"
                data-testid="idea-impact-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="text-white" style={{ background: C.burgundy }} data-testid="idea-submit-btn">
              <Send className="mr-1.5 h-4 w-4" /> {saving ? 'Submitting…' : 'Submit idea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
